import React from 'react';
import IconElements from '@/assets/icons/icon-elements.svg?react';
import { PrimaryButton } from '@/components/buttons/PrimaryButton/PrimaryButton';
import { SecondaryButton } from '@/components/buttons/SecondaryButton/SecondaryButton';
import IconAdd from '@/assets/icons/icon-add.svg?react';
import IconDelete from '@/assets/icons/icon-delete.svg?react';
import { Pencil } from 'lucide-react';
import clsx from 'clsx';
import type { BackgroundImageMode, BackgroundPosition } from '@builder4/shared/types/layout';

interface ImagePreviewProps {
  image?: string | null;
  onReplaceClick: () => void;
  onRemoveClick: () => void;
  opacity?: number;
  mode?: BackgroundImageMode;
  position?: BackgroundPosition;
  scale?: number;
}

export const ImagePreview: React.FC<ImagePreviewProps> = ({
  image = null,
  onReplaceClick,
  onRemoveClick,
  opacity = 100,
  mode = 'fill',
  position = 'center center',
  scale = 100,
}) => {
  const modeStyles = {
    fill: { backgroundSize: 'cover', backgroundRepeat: 'no-repeat' },
    fit: { backgroundSize: 'contain', backgroundRepeat: 'no-repeat' },
    stretch: { backgroundSize: '100% 100%', backgroundRepeat: 'no-repeat' },
    tile: { backgroundSize: `${scale}% ${scale}%`, backgroundRepeat: 'repeat' },
  };

  return (
    <div
      className={clsx(
        'group bg-neutral-10 relative inline-flex h-31 w-54 cursor-pointer flex-col items-center justify-center gap-2.5 rounded px-12 py-4.5 outline outline-offset-[-1px] outline-neutral-100',
      )}
    >
      <div
        className={clsx('flex h-20 w-30 items-center justify-center overflow-hidden rounded-lg', {
          'bg-emerald-500/20 outline outline-offset-[-1px] outline-green-500 outline-dashed':
            !image,
        })}
        style={
          image
            ? {
                backgroundImage: `url(${image})`,
                opacity: opacity / 100,
                backgroundPosition: position,
                ...modeStyles[mode],
              }
            : undefined
        }
      >
        {!image && <IconElements className="h-6 w-6 text-[#10B96DD9]" />}
      </div>

      <div className="absolute top-0 left-0 z-10 flex h-full w-full items-center justify-center gap-2 rounded border border-indigo-500 bg-indigo-50 opacity-0 transition-all group-hover:opacity-100">
        {image ? (
          <>
            <PrimaryButton text="Edit" size="sm" icon={Pencil} onClick={onReplaceClick} />
            <SecondaryButton text="Remove" size="sm" icon={IconDelete} onClick={onRemoveClick} />
          </>
        ) : (
          <PrimaryButton size="sm" text="Choose Image" icon={IconAdd} onClick={onReplaceClick} />
        )}
      </div>
    </div>
  );
};
